"use client";

import type React from "react";
import { useEffect, useRef, useState } from "react";
import { Check, Link2, Mail, Share2 } from "lucide-react";
import { cn } from "./cn";
import { useDict } from "@/components/i18n/DictProvider";

/**
 * Share affordance for content pages — the sharing-standards dossier
 * (2026-09-01) turned into a component:
 *
 * - NATIVE FIRST: `navigator.share` (a W3C Recommendation, Interop 2026
 *   focus) opens the OS sheet on a user gesture — which is also the ONLY
 *   legitimate route to Instagram and TikTok: neither platform offers a web
 *   compose-from-link endpoint, so they appear exactly where their apps are,
 *   in the sheet (and via copy-link). The button renders only where the API
 *   exists (feature-detected after mount; SSR renders without it).
 * - INTENT-URL FALLBACK: the provider-sanctioned endpoints and nothing else —
 *   Facebook sharer (previews come from our OG tags alone), the X web
 *   intent, LinkedIn share-offsite, WhatsApp click-to-chat, mailto, and
 *   copy-link (the most used "share" of all). No SDKs, no widgets, no
 *   counts, no UTM decoration — each rejected in the dossier with reasons.
 * - The URL must be ABSOLUTE (callers pass `absoluteUrl(...)`) and the
 *   payload only ever carries the PUBLIC surface (title + abstract) — gated
 *   body text never enters a share intent.
 *
 * Two variants: `row` (article headers — the icon strip) and `circle` (the
 * detail sidebars' size-12 action-circle vocabulary; unsupported native
 * share expands the row inline beneath it).
 *
 * PRIVATE portals render no share at all — the CALLER's gate
 * (`isPrivatePortal`), decided once in the dossier: intranet links land
 * outsiders on a login wall, and inviting employees to post internal URLs
 * to social networks is a leak vector, not a feature.
 */

/** Brand glyphs, inlined — the installed lucide dropped its deprecated brand
 *  set entirely (Facebook/Twitter/Linkedin are gone), and a generic icon
 *  would misname the platform. Same stroke vocabulary as FooterView's
 *  SocialGlyph. */
function stroke(children: React.ReactNode, className?: string) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={className}
    >
      {children}
    </svg>
  );
}
function FacebookGlyph({ className }: { className?: string }) {
  return stroke(
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />,
    className,
  );
}
function XGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className={className}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}
function LinkedInGlyph({ className }: { className?: string }) {
  return stroke(
    <>
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
      <rect width="4" height="12" x="2" y="9" />
      <circle cx="4" cy="4" r="2" />
    </>,
    className,
  );
}

/** WhatsApp brand glyph. */
function WhatsAppGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className={className}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
    </svg>
  );
}

export function ShareActions({
  url,
  title,
  text,
  variant = "row",
  className,
}: {
  /** Absolute canonical URL — intents need the full origin. */
  url: string;
  title: string;
  /** Public abstract only — never gated body text. */
  text?: string;
  /** `row` the open intent row, `circle` the sidebars' 48 px disc, `compact` a
   *  single icon button sized for the reader strip — the intents open in a
   *  panel OVER the article, never pushing it down (the strip is sticky and
   *  publishes its own height as `--reader-tools-h`). */
  variant?: "row" | "circle" | "compact";
  className?: string;
}) {
  const t = useDict();
  const [canNative, setCanNative] = useState(false);
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Feature detection MUST happen post-hydration: navigator.share differs
    // between server (absent) and client, so deriving it in render (or a
    // useState initializer) would mismatch hydration. One mount-time set.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCanNative(typeof navigator !== "undefined" && typeof navigator.share === "function");
    return () => {
      if (copyTimer.current) clearTimeout(copyTimer.current);
    };
  }, []);

  // An open intent panel covers the article, so it has to be dismissible the
  // two ways a reader will try: Escape, and a click anywhere else. Only wired
  // while it is open (the row and circle variants never set `expanded` from a
  // panel that overlays anything).
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setExpanded(false);
    };
    const onDown = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setExpanded(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown);
    };
  }, [expanded]);

  /**
   * True when the sheet actually opened — including when the reader dismissed
   * it, which is a finished gesture, not a failure. False means it never
   * opened and the caller must offer the intent row instead.
   *
   * `navigator.share` EXISTING does not mean it works: desktop Chrome defines
   * it and can still reject (no OS target, the payload refused, a policy
   * block). Until 2026-09-20 every rejection was swallowed here, so on such a
   * browser the button did nothing at all — invisible while the intent icons
   * sat next to it, fatal once the reader strip made this the only share
   * control. `canShare` is asked first where it exists, and the rest is caught.
   */
  async function nativeShare(): Promise<boolean> {
    const data = { title, text: text || title, url };
    try {
      if (typeof navigator.canShare === "function" && !navigator.canShare(data)) return false;
      await navigator.share(data);
      return true;
    } catch (err) {
      return err instanceof DOMException && err.name === "AbortError";
    }
  }

  /** Native sheet when it really opens, the intent row when it does not. */
  async function shareOrExpand() {
    if (canNative && (await nativeShare())) return;
    setExpanded((e) => !e);
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      if (copyTimer.current) clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — the intent links still work */
    }
  }

  const u = encodeURIComponent(url);
  const s = encodeURIComponent(text ? `${title} — ${text}` : title);
  const intents: {
    label: string;
    href: string;
    Icon: React.ComponentType<{ className?: string }>;
  }[] = [
    {
      label: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
      Icon: FacebookGlyph,
    },
    {
      label: "X",
      href: `https://twitter.com/intent/tweet?url=${u}&text=${encodeURIComponent(title)}`,
      Icon: XGlyph,
    },
    {
      label: "LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
      Icon: LinkedInGlyph,
    },
    { label: "WhatsApp", href: `https://wa.me/?text=${s}%20${u}`, Icon: WhatsAppGlyph },
    {
      label: "E-mail",
      href: `mailto:?subject=${encodeURIComponent(title)}&body=${s}%0A%0A${u}`,
      Icon: Mail,
    },
  ];

  const iconBtn =
    "grid size-8 place-items-center rounded-full text-medium-gray transition-colors hover:bg-muted hover:text-primary";

  const row = (
    <div className="flex flex-wrap items-center gap-1" role="group" aria-label={t.detail.share}>
      {canNative ? (
        <button
          type="button"
          onClick={() => void nativeShare()}
          aria-label={t.detail.share}
          title={t.detail.share}
          className={iconBtn}
        >
          <Share2 className="size-4" aria-hidden />
        </button>
      ) : null}
      {intents.map(({ label, href, Icon }) => (
        <a
          key={label}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          title={label}
          className={iconBtn}
        >
          <Icon className="size-4" aria-hidden />
        </a>
      ))}
      <button
        type="button"
        onClick={copyLink}
        aria-label={copied ? t.detail.linkCopied : t.detail.share}
        title={copied ? t.detail.linkCopied : t.detail.share}
        className={cn(iconBtn, copied && "text-success hover:text-success")}
      >
        {copied ? (
          <Check className="size-4" aria-hidden />
        ) : (
          <Link2 className="size-4" aria-hidden />
        )}
      </button>
      <span aria-live="polite" className="sr-only">
        {copied ? t.detail.linkCopied : ""}
      </span>
    </div>
  );

  if (variant === "row") return <div className={className}>{row}</div>;

  if (variant === "compact") {
    return (
      <div ref={wrapRef} className={cn("relative", className)}>
        <button
          type="button"
          onClick={shareOrExpand}
          aria-expanded={expanded}
          aria-label={t.detail.share}
          title={t.detail.share}
          className={cn(
            // The reader strip's own button vocabulary (ReaderTools.iconBtn).
            "text-muted-foreground hover:text-foreground focus-visible:ring-ring bg-muted hover:bg-border/60 inline-flex size-9 items-center justify-center rounded-md transition-colors pointer-coarse:size-11 focus-visible:ring-2 focus-visible:outline-none",
            expanded && "text-foreground",
          )}
        >
          <Share2 className="size-4" aria-hidden />
        </button>
        {/* `w-max`: an absolutely positioned box shrink-to-fits against its
            CONTAINING BLOCK, which here is the 36 px button wrapper — without
            it the intent row wraps to one icon per line. */}
        {expanded ? (
          <div className="border-border/60 bg-card absolute top-full right-0 z-40 mt-1 w-max rounded-lg border p-1.5 shadow-lg">
            {row}
          </div>
        ) : null}
      </div>
    );
  }

  // circle — the detail sidebars' action vocabulary (MediaSidebar's size-12
  // circles). Native share when it opens; otherwise the click expands the
  // intent row beneath the circle cluster.
  //
  // `wrapRef` belongs here too, not only on the compact panel: the dismiss
  // effect asks whether the pointer landed inside it, and a null ref reads as
  // "outside", which would collapse the row under the reader's own click on a
  // share link.
  return (
    <div ref={wrapRef} className={className}>
      <button
        type="button"
        onClick={shareOrExpand}
        aria-expanded={expanded}
        aria-label={t.detail.share}
        title={t.detail.share}
        className={cn(
          "grid size-12 shrink-0 place-items-center rounded-full border transition-colors",
          expanded
            ? "border-accent/30 bg-accent/15 text-accent"
            : "border-border/60 text-foreground/75 hover:bg-foreground/[0.04] hover:text-foreground",
        )}
      >
        <Share2 className="size-5" aria-hidden />
      </button>
      {expanded ? <div className="mt-2">{row}</div> : null}
    </div>
  );
}
